// Styles
import "./QAFindingsTab.scss";

// React
import { useState } from "react";

// Icons
import ChevronLeftIcon from "../../_assets/icons/ChevronLeftIcon";
import ChevronRightIcon from "../../_assets/icons/ChevronRightIcon";
import FirstPageIcon from "../../_assets/icons/FirstPageIcon";
import LastPageIcon from "../../_assets/icons/LastPageIcon";
import SortIcon from "../../_assets/icons/SortIcon";

// UI Components
import {
  Headline2,
  Label2,
  Subtitle2,
} from "@bms-ai/bms-ai-ui-lib/components";

// Types
interface CheckWiseCount {
  check_name: string;
  pass: number;
  fail: number;
  pass_rate: number;
}

interface FindingsSummary {
  total_checks: number;
  pass_rate: number;
  passed: number;
  failed: number;
  check_wise_count: CheckWiseCount[];
}

interface QAFindingsTabProps {
  findingsSummary: FindingsSummary | null;
  isFindingsLoading: boolean;
}

const QAFindingsTab: React.FC<QAFindingsTabProps> = ({
  findingsSummary,
  isFindingsLoading,
}) => {
  const [sortConfig, setSortConfig] = useState<{
    key: "check_name" | "pass" | "fail" | "pass_rate";
    direction: "asc" | "desc";
  } | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(10);

  const handleSort = (key: "check_name" | "pass" | "fail" | "pass_rate") => {
    let direction: "asc" | "desc" = "asc";

    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }

    setSortConfig({ key, direction });
    setCurrentPage(1); // Reset to first page when sorting
  };

  const getSortedData = () => {
    if (!findingsSummary) {
      return [];
    }

    const sortedData = [...findingsSummary.check_wise_count];

    if (sortConfig) {
      sortedData.sort((a, b) => {
        if (sortConfig.key === "check_name") {
          // Alphabetical sorting for check name
          const aValue = a[sortConfig.key] as string;
          const bValue = b[sortConfig.key] as string;
          return sortConfig.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else {
          // Numeric sorting for pass, fail, pass_rate
          const aValue = a[sortConfig.key] as number;
          const bValue = b[sortConfig.key] as number;
          return sortConfig.direction === "asc"
            ? aValue - bValue
            : bValue - aValue;
        }
      });
    }

    return sortedData;
  };

  const getPaginatedData = () => {
    const sortedData = getSortedData();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedData.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    const sortedData = getSortedData();
    return Math.ceil(sortedData.length / itemsPerPage);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getPageRange = () => {
    const sortedData = getSortedData();
    const totalItems = sortedData.length;
    const startIndex = (currentPage - 1) * itemsPerPage + 1;
    const endIndex = Math.min(currentPage * itemsPerPage, totalItems);
    return { startIndex, endIndex, totalItems };
  };

  if (isFindingsLoading) {
    return (
      <div className="qa-findings-container">
        <div className="loading-state">
          <Subtitle2>Loading QA findings...</Subtitle2>
        </div>
      </div>
    );
  }

  if (!findingsSummary) {
    return (
      <div className="qa-findings-container">
        <div className="no-data-state">
          <Subtitle2>No QA findings data available</Subtitle2>
        </div>
      </div>
    );
  }

  return (
    <div className="qa-findings-container">
      {/* Summary Stats */}
      <div className="findings-summary-stats">
        <div className="summary-stat-card">
          <Label2 className="stat-label">Total Checks</Label2>
          <Headline2 className="stat-value">
            {findingsSummary.total_checks}
          </Headline2>
        </div>
        <div className="summary-stat-card">
          <Label2 className="stat-label">Pass Rate</Label2>
          <Headline2 className="stat-value">
            {findingsSummary.pass_rate.toFixed(1)}%
          </Headline2>
        </div>
        <div className="summary-stat-card success">
          <Label2 className="stat-label">Passed</Label2>
          <Headline2 className="stat-value">
            {findingsSummary.passed}
          </Headline2>
        </div>
        <div className="summary-stat-card error">
          <Label2 className="stat-label">Failed</Label2>
          <Headline2 className="stat-value">
            {findingsSummary.failed}
          </Headline2>
        </div>
      </div>

      {/* Check-wise Breakdown Table */}
      <div className="findings-table-container">
        <table className="findings-table">
          <thead>
            <tr>
              <th
                onClick={() => handleSort("check_name")}
                className="sortable-header"
              >
                <div className="header-content">
                  <span>Check Name</span>
                  <div className="sort-icons">
                    <SortIcon width={20} height={20} />
                  </div>
                </div>
              </th>
              <th
                onClick={() => handleSort("pass")}
                className="sortable-header"
              >
                <div className="header-content">
                  <span>Pass</span>
                  <div className="sort-icons">
                    <SortIcon width={20} height={20} />
                  </div>
                </div>
              </th>
              <th
                onClick={() => handleSort("fail")}
                className="sortable-header"
              >
                <div className="header-content">
                  <span>Fail</span>
                  <div className="sort-icons">
                    <SortIcon width={20} height={20} />
                  </div>
                </div>
              </th>
              <th
                onClick={() => handleSort("pass_rate")}
                className="sortable-header"
              >
                <div className="header-content">
                  <span>Pass Rate</span>
                  <div className="sort-icons">
                    <SortIcon width={20} height={20} />
                  </div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {getPaginatedData().map((check, index) => (
              <tr key={index}>
                <td>
                  <Label2 strong>{check.check_name}</Label2>
                </td>
                <td>
                  <span className="pass-count">{check.pass}</span>
                </td>
                <td>
                  <span className="fail-count">{check.fail}</span>
                </td>
                <td>
                  <div className="pass-rate-cell">
                    <span className="pass-rate-value">
                      {check.pass_rate.toFixed(1)}%
                    </span>
                    <div className="pass-rate-bar">
                      <div
                        className="pass-rate-fill"
                        style={{ width: `${check.pass_rate}%` }}
                      ></div>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {getTotalPages() > 1 && (
          <div className="findings-pagination">
            <div className="pagination-info">
              <span>
                {getPageRange().startIndex} to{" "}
                {getPageRange().endIndex} of{" "}
                {getPageRange().totalItems}
              </span>
            </div>
            <div className="pagination-controls">
              <button
                className="page-nav-btn"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                title="First page"
              >
                <FirstPageIcon width={16} height={16} />
              </button>
              <button
                className="page-nav-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                title="Previous page"
              >
                <ChevronLeftIcon width={16} height={16} />
              </button>
              <span className="page-indicator">
                Page {currentPage} of {getTotalPages()}
              </span>
              <button
                className="page-nav-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === getTotalPages()}
                title="Next page"
              >
                <ChevronRightIcon width={16} height={16} />
              </button>
              <button
                className="page-nav-btn"
                onClick={() => handlePageChange(getTotalPages())}
                disabled={currentPage === getTotalPages()}
                title="Last page"
              >
                <LastPageIcon width={16} height={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QAFindingsTab;
